import { API_DOMAIN } from "@monorepo/constants";
import {
  IEnterCampaignRequest,
  IFaq,
  IGetCampaignResponse,
  IPolicy,
  IPolicyResponse,
  ISuggestionModalResponse,
  ISupportInfoResponse,
  TGetSettings,
} from "@monorepo/types";
import { createApi } from "@reduxjs/toolkit/query/react";

import { customBaseQuery } from "../../helpers/baseQuery";

export const miscApi = createApi({
  reducerPath: "miscApi",
  baseQuery: customBaseQuery<{
    data: IPolicyResponse | TGetSettings;
    meta: ISupportInfoResponse | ISuggestionModalResponse;
    included: Array<IFaq>;
  }>({
    baseUrl: API_DOMAIN,
  }),
  tagTypes: ["Campaign", "Settings"],
  endpoints: (builder) => ({
    legalPolicy: builder.query<IPolicy, void>({
      query: () => ({
        url: "/api/v1/pages/policy",
      }),
    }),
    privacyPolicy: builder.query<IPolicy, void>({
      query: () => ({
        url: "/api/v1/pages/privacy-policy",
      }),
    }),
    faqs: builder.query<IFaq, void>({
      query: () => ({
        url: "/api/v1/faq-groups",
      }),
      extraOptions: {
        serializeResponse: false,
      },
    }),
    suggestionModal: builder.query<ISuggestionModalResponse, { cartGroupId: string; increment?: boolean }>({
      query: ({ cartGroupId, increment }) => ({
        url: "/api/v1/service-suggestion",
        params: {
          cart_group_id: cartGroupId,
          increment,
        },
      }),
      transformResponse: (response) => {
        return response.meta as ISuggestionModalResponse;
      },
      extraOptions: {
        serializeResponse: false,
      },
    }),
    support: builder.query<ISupportInfoResponse, void>({
      query: () => ({
        url: "/api/v1/support/info",
      }),
      extraOptions: {
        serializeResponse: false,
      },
      transformResponse: (response) => {
        return response?.meta as ISupportInfoResponse;
      },
    }),
    getCampaignByBarcode: builder.query<IGetCampaignResponse, { barcode: string }>({
      query: ({ barcode }) => ({
        url: `/api/v1/campaigns/find-by-barcode?barcode=PGO-CAMP-${barcode}`,
      }),
      providesTags: ["Campaign"],
    }),
    enterCampaign: builder.mutation<void, IEnterCampaignRequest>({
      query: (body) => ({
        url: "/api/v1/campaigns/register",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Campaign"],
    }),
    getSettings: builder.query<TGetSettings, void>({
      query: () => ({
        url: "api/v1/settings",
      }),
      extraOptions: {
        serializeResponse: false,
      },
      providesTags: ["Settings"],
    }),
  }),
});

export const {
  usePrivacyPolicyQuery,
  useLegalPolicyQuery,
  useFaqsQuery,
  useSupportQuery,
  useSuggestionModalQuery,
  useLazyGetCampaignByBarcodeQuery,
  useEnterCampaignMutation,
  useGetSettingsQuery,
} = miscApi;

export default miscApi.reducer;
